'use client'

import { cn } from '@/lib/utils'
import { useStoreBanner } from '@/store'
import { Typo3Banner } from '@/types/typo3'
import { Icon } from '@/ui/components'
import { delay } from '@/utils'
import { AnimatePresence, motion } from 'motion/react'
import { memo, useEffect, useState } from 'react'
import Button from '../Button/Button'

interface Props {
  data: Typo3Banner
}

const Banner = ({ data }: Props) => {
  //Stores
  const { isHidden, setIsHidden, firstRender, setFirstRender } =
    useStoreBanner()

  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setTimeout(() => {
      setIsMounted(true)
    }, 1000)
  }, [])

  if (!data || !data?.length || !firstRender) {
    return null
  }

  const colorClasses = {
    red: 'bg-feedback-red text-neutral-100',
    yellow: 'bg-feedback-orange text-neutral-100',
    green: 'bg-feedback-green text-neutral-100',
    default: 'bg-neutral-100 shadow-xl border border-neutral-200'
  }

  const color = data?.[0]?.color || 'default'

  //handlers
  const handleHidden = async () => {
    if (!isHidden) {
      setIsHidden(true)
      await delay(1000)
      setFirstRender(false)
    }
  }

  return (
    <AnimatePresence initial={false}>
      {!isHidden && isMounted && (
        <motion.div
          className={cn(
            'textstyle-sm-regular fixed bottom-0 left-0 z-750 w-full'
          )}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          key={'banner'}
        >
          <div className="p-xs mx-auto max-w-[2200px]">
            <div
              className={cn(
                'p-gutter pt-md pb-md relative flex justify-center',
                colorClasses[color as keyof typeof colorClasses] ||
                  colorClasses.default
              )}
            >
              <div className="gap-md max-lg:flex max-lg:flex-col">
                <Icon
                  className="mr-sm -mt-2xs size-5 lg:inline"
                  icon={
                    color === 'green'
                      ? 'material-symbols:done'
                      : color === 'default'
                        ? 'material-symbols:info'
                        : 'mdi:alert'
                  }
                />
                <div className="pr-sm lg:inline">
                  <p className="textstyle-sm-bold pr-sm lg:inline">
                    {data[0].title}
                  </p>
                  {data[0].description}
                </div>
                <div className="lg:inline">
                  <Button
                    size="s"
                    variant="text"
                    href={data[0].link.href}
                    target={data[0].link.target || '_blank'}
                    nextLink={data[0].link.target === '_blank' ? false : true}
                    isTransition={
                      data[0].link.target === '_blank' ? false : true
                    }
                    icon="material-symbols:chevron-right"
                    iconPosition="right"
                    mode={color === 'default' ? undefined : 'invert'}
                    className="button grow whitespace-nowrap"
                  >
                    {data[0].link.linkText}
                  </Button>
                </div>
              </div>
              <button
                className="absolute top-0 right-0 flex h-8 w-8 cursor-pointer items-center justify-center [&_svg]:h-5 [&_svg]:w-5 [&_svg]:transition-transform [&_svg]:duration-(--duration-default) [&_svg]:ease-(--ease-out-cubic) hover:[&_svg]:rotate-180"
                onClick={handleHidden}
                aria-label="Close banner"
              >
                <Icon icon="material-symbols:close" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default memo(Banner)
